import type { PaletteMode } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ShadesColor } from '@/mui/types';

import { ALPHA_300, ALPHA_500, ALPHA_700 } from '../../opacity';
import { ACTIONS_OPACITY } from '../index';
// export const TAG_PEOPLE = '#1A8236';
// export const TAG_BRANDS = '#1C5F99';
// export const TAG_IAB = '#8E2CA7';
// export const TAG_BRAND_SAFETY = '#B63104';
// export const TAG_KEYWORDS = '#155766';
// export const TAG_TEXT = '#785020';
// export const TAG_CC = '#5B2CC4';

export const TAG_PEOPLE = {
  dark: '#BDFDD7',
  main: '#DAFDE8',
  light: '#E8FFF1',
  contrastText: '#1A8236',
};

export const TAG_BRANDS = {
  dark: '#CCDAF0',
  main: '#DBE8FB',
  light: '#E7F0FF',
  contrastText: '#1C5F99',
};

export const TAG_IAB = {
  dark: '#F3C5FF',
  main: '#F8DBFF',
  light: '#FBE9FF',
  contrastText: '#8E2CA7',
};

export const TAG_BRAND_SAFETY = {
  dark: '#FFCCBB',
  main: '#FFDFD3',
  light: '#FFEAE2',
  contrastText: '#B63104',
};

export const TAG_KEYWORDS = {
  dark: '#ABF0FF',
  main: '#C7F5FF',
  light: '#D9F8FF',
  contrastText: '#155766',
};

export const TAG_TEXT = {
  dark: '#FFDAAE',
  main: '#FFE6C9',
  light: '#FFEDD9',
  contrastText: '#785020',
};

export const TAG_CC = {
  dark: '#D4C2FE',
  main: '#E2D7FF',
  light: '#EBE3FF',
  contrastText: '#5B2CC4',
};

const SHADES: ShadesColor = {
  0: '#FFFFFF',
  50: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  A100: '#FFFFFF',
  A200: '#FFFFFF',
  A400: '#FFFFFF',
  A700: '#FFFFFF',
};

const PRIMARY = {
  dark: '#0D31B8',
  main: '#2F56E7',
  light: '#497AF7',
  contrastText: '#fff',
};

const SECONDARY = {
  dark: '#6719DF',
  main: '#782CEC',
  light: '#8744EF',
  contrastText: '#fff',
};

const HIGHLIGHT = {
  dark: '#FFD500',
  main: '#FFE660',
  light: '#FFF0A4',
  contrastText: '#000',
};

const HIGHLIGHT_2 = {
  dark: '#0293EA',
  main: '#00A0FF',
  light: '#32B3FF',
  contrastText: '#fff',
};

const COMPLIMENTARY = {
  dark: '#37C7B9',
  main: '#4CDDCF',
  light: '#75FBEB',
  contrastText: '#000',
};

const ERROR = {
  dark: '#CA2520',
  main: '#DD3630',
  light: '#EB5050',
  contrastText: '#fff',
};

const WARNING = {
  dark: '#FF8B04',
  main: '#FFA234',
  light: '#FFB72C',
  contrastText: '#000',
};

const SUCCESS = {
  dark: '#469036',
  main: '#57AF48',
  light: '#66CD54',
  contrastText: '#fff',
};

const INFO = {
  dark: '#7E8AB7',
  main: '#929EC8',
  light: '#ACB9DA',
  contrastText: '#000',
};

const BACKGROUND = {
  paper: '#fff',
  default: '#fbfbfb',
};

const TEXT = {
  primary: '#000',
  secondary: alpha('#000', ALPHA_700),
  disabled: alpha('#000', ALPHA_500),
};

const GRAPHS = [
  '#7439D4', // 0
  '#C05BA4', // 1
  '#20914D', // 2
  '#1A658F', // 3
  '#637595', // 4
  '#71AED2', // 5
  '#8A502A', // 6
  '#E53436', // 7
  '#84EB34', // 8
  '#F0AD54', // 9
  '#9748FF', // 10
  '#FC73FF', // 11
  '#9BE757', // 12
  // Marketplace graphs
  '#2F56E7', // 13
  '#2BD7CC', // 14
  '#06A0FF', // 15
  '#FF4702', // 16
];

const GRADIENT = ['#A1B6E0'];

const PLATFORM = {
  anyclip: '#663d9e',
  youtube: '#ff0000',
  linkedin: '#2867b2',
  facebook: '#1877f2',
  twitter: '#000000',
  whatsapp: '#25d366',
  default: '#6782a4',
};

const palette = {
  mode: 'light' as PaletteMode,
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  highlight: HIGHLIGHT,
  highlight2: HIGHLIGHT_2,
  error: ERROR,
  complimentary: COMPLIMENTARY,
  text: TEXT,

  gradient: GRADIENT,

  platform: PLATFORM,

  '-tagText': TAG_TEXT,
  '-tagBrandSafety': TAG_BRAND_SAFETY,
  '-tagBrands': TAG_BRANDS,
  '-tagIAB': TAG_IAB,
  '-tagPeople': TAG_PEOPLE,
  '-tagKeywords': TAG_KEYWORDS,
  '-tagCC': TAG_CC,

  '-graph': GRAPHS,

  divider: alpha(SHADES[900], ALPHA_300),
  background: BACKGROUND,
  shades: SHADES,
  action: {
    active: SHADES[900],
    hover: alpha(SHADES[900], ACTIONS_OPACITY.hoverOpacity),
    selected: alpha(SHADES[900], ACTIONS_OPACITY.selectedOpacity),
    disabled: alpha(SHADES[900], ACTIONS_OPACITY.disabledOpacity),
    disabledBackground: alpha(SHADES[900], ACTIONS_OPACITY.disabledBackgroundOpacity),
    focus: alpha(SHADES[900], ACTIONS_OPACITY.focusOpacity),
    ...ACTIONS_OPACITY,
  },
};

export type PaletteType = typeof palette;

export default palette;
